import {
  EventEmitter,
  Component,
  Input,
  Output,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import {
  PasscodeChallenge,
  PasscodeDigit,
} from '../../../../services/authentication.service';
import { BrandedRouter } from '../../../../services/brandedRouter';
import {
  AuthenticationFactor,
  StepUpModel,
  IOtpResponse,
  IStepUpResult,
  IScaChallenge,
  OtpChannel,
} from '../../../../services/strong-customer-authentication/model';
import { PasscodeComponent } from '../../../passcode/passcode.component';
import { AccountSummaryService } from '../../../../services/accountSummary.service';
import { BrowserService } from '../../../../services/browser.service';
import { AuthenticatedScaOptionsService } from './authenticated-sca-options.service';
import { BrandService } from '../../../../services/brand.service';
import { PopUpComponent } from '../../../pop-up/pop-up.component';
import { AssetConfigVariableService } from '../../../../services/asset-config-variable.service';

export enum ScaModuleStep {
  Passcode = 'Passcode',
  PhoneSelect = 'PhoneSelect',
  CodeValidation = 'CodeValidation',
  PhoneCalling = 'PhoneCalling',
  Error = 'Error',
  Authenticated = 'Authenticated',
}

@Component({
  selector: 'authenticated-sca-options',
  templateUrl: './authenticated-sca-options.component.html',
})
export class AuthenticatedSCAOptionsComponent implements AfterViewInit {
  OtpChannel = OtpChannel;
  AuthenticationFactor = AuthenticationFactor;
  ScaModuleStep: typeof ScaModuleStep = ScaModuleStep;
  @Output()
  hasScaOptions = new EventEmitter();
  @Output()
  submitted = new EventEmitter();
  @Output()
  tryAgainClicked = new EventEmitter();
  @Output()
  onError = new EventEmitter();
  @Output()
  onClose = new EventEmitter();
  @Input()
  set scaChallenge(scaChallenge: IScaChallenge) {
    this._scaChallenge = scaChallenge;
    if (scaChallenge) {
      if (!this.form) {
        this.init();
      } else {
        this.resetForm();
      }
    }
  }
  get scaChallenge(): IScaChallenge {
    return this._scaChallenge;
  }
  @Input()
  isSubmitting: boolean;
  @Input()
  set submitResult(submitResult: IStepUpResult) {
    if (!submitResult) {
      return;
    }

    if (typeof submitResult === 'boolean' && submitResult === true) {
      this.goToStep(ScaModuleStep.Authenticated);
      return;
    }

    if (submitResult.accountLocked) {
      this.accountLocked = true;
    }

    this._submitResult = submitResult;

    if (
      this._submitResult.accountLocked === false &&
      this._submitResult.isSuccess === false
    ) {
      if (this.showErrors) {
        this.goToStep(ScaModuleStep.Error);
      } else {
        this.onError.emit();
      }
    }
  }
  get submitResult(): IStepUpResult {
    return this._submitResult;
  }

  @Input()
  showErrors = true;

  public imageRootPath: string = '';
  authenticationFactor: AuthenticationFactor;
  availableFactors: AuthenticationFactor[];
  passcodeInputs;
  contactUsLink: string;
  otpResponse: IOtpResponse;
  form: FormGroup;
  step: ScaModuleStep;
  mobileNumber: string;
  homeNumber: string;
  isResend = false;
  notMyNumberDropdownOpen = false;
  error = false;
  accountLocked = false;
  brand: string;
  @ViewChild('passcode') passcodeComponent: PasscodeComponent;
  @ViewChild('popup', { static: true }) popUpComponent: PopUpComponent;

  private _submitResult: IStepUpResult;
  private _scaChallenge: IScaChallenge;
  private passcodeFormControl: FormControl;
  private otpChannelFormControl: FormControl;
  private initialized = false;

  constructor(
    private scaService: AuthenticatedScaOptionsService,
    private accountSummaryService: AccountSummaryService,
    private browserService: BrowserService,
    private assetConfigVariableService: AssetConfigVariableService,
    brandService: BrandService,
    brandedRouter: BrandedRouter
  ) {
    this.contactUsLink = brandedRouter.getBrandedLink('/help/contact-us');
    this.brand = brandService.getBrand();
  }

  async ngOnInit() {
    this.imageRootPath =
      await this.assetConfigVariableService.getImageRootPath();
  }

  init(): void {
    this.form = new FormGroup({});

    if (
      !this.scaChallenge.isOtpRequired &&
      !this.scaChallenge.passcodeChallenge
    ) {
      this.authenticate();
      return;
    }

    if (this.scaChallenge.isOtpRequired) {
      this.initPhoneNumbers();
      this.setOtpAuthenticationFactor();
    }

    if (this.scaChallenge.passcodeChallenge) {
      this.goToStep(ScaModuleStep.Passcode);
      this.initPasscodeField();
    } else {
      this.setUpPhoneSelection();
    }

    this.emitScaOptions();
  }

  ngAfterViewInit(): void {
    this.initialized = true;
    this.emitScaOptions();
  }

  closePopup(): void {
    this.browserService.setPopUpActive(false, false);
    this.onClose.emit();
  }

  refreshPopupSize(): void {
    setTimeout(() => this.browserService.incrementResizeCount());
  }

  getStepUpModel(): StepUpModel {
    this.touchControls();

    if (this.form.invalid) {
      return;
    }

    const scaData: StepUpModel = {
      passcodeDigits: this.getPasscodeValue(),
      verificationToken: this.scaChallenge.verificationToken,
    };

    if (this.authenticationFactor === AuthenticationFactor.OTP) {
      scaData.otpValue = this.scaChallenge.isOtpRequired
        ? this.form.controls[AuthenticationFactor.OTP].value
        : undefined;
    }

    return scaData;
  }

  async requestResendOTP(): Promise<void> {
    if (this.isSubmitting) {
      return;
    }

    this.error = false;
    this.form.controls[AuthenticationFactor.OTP].reset();

    try {
      this.otpResponse = await this.scaService.requestResendOTP(
        this.scaChallenge.verificationToken,
        this.otpChannelFormControl.value
      );

      this.scaChallenge.verificationToken = this.otpResponse.verificationToken;
      this.scaChallenge.canResend = this.otpResponse.canResend;

      this.isResend = true;
    } catch {
      this.error = true;
    }
  }

  async callAgain(): Promise<void> {
    this.otpChannelFormControl.setValue(OtpChannel.Call);
    await this.requestResendOTP();
    this.goToCallScreen();
  }

  continue(): void {
    this.passcodeComponent.submit();

    if (this.scaChallenge.isOtpRequired) {
      this.setUpPhoneSelection();
    } else {
      this.submitted.emit(this.getStepUpModel());
    }
  }

  async sendCode(): Promise<void> {
    this.error = false;
    this.isSubmitting = true;
    this.refreshModal();

    try {
      this.otpResponse = await this.scaService.requestSendOTP(
        this.scaChallenge.verificationToken,
        this.otpChannelFormControl.value
      );

      this.scaChallenge.verificationToken = this.otpResponse.verificationToken;
      this.scaChallenge.canResend = this.otpResponse.canResend;
      this.isSubmitting = false;

      if (this.otpChannelFormControl.value === OtpChannel.Call) {
        this.goToCallScreen();
      } else {
        this.goToStep(ScaModuleStep.CodeValidation);
      }
    } catch {
      this.setErrorMessage();
    }
  }

  authenticate(): void {
    this.submitted.emit(this.getStepUpModel());
  }

  tryAgain(): void {
    this.tryAgainClicked.emit();
    this.isSubmitting = true;
    this.scaChallenge = this.submitResult = undefined;
  }

  isPasscodeValid(): boolean {
    return (
      this.passcodeFormControl &&
      this.passcodeFormControl.valid &&
      this.passcodeComponent &&
      this.passcodeComponent.inputted
    );
  }

  getErrorMessage(): string {
    if (this.otpResponse && !this.otpResponse.canResend) {
      return '<p><b>You have reached your resend limit.</b></p>Please enter one of the OTP codes previously sent to you.';
    } else if (this.submitResult) {
      if (this.submitResult.remainingAttempts) {
        return `<p><b>You have ${this.submitResult.remainingAttempts} ${
          this.submitResult.remainingAttempts > 1 ? 'tries' : 'try'
        } remaining.</b></p>
                    ${
                      this.submitResult.remainingAttempts === 1
                        ? 'Your account will be locked if incorrect.'
                        : 'At least one of the credentials you have entered is incorrect. Please try again.'
                    } `;
      }
    }
  }

  getSelectedOtpChannel(): OtpChannel {
    if (!this.otpChannelFormControl) {
      return OtpChannel.Text;
    }

    return this.otpChannelFormControl.value || OtpChannel.Text;
  }

  get isDisabled(): boolean {
    return this.form && this.form.invalid;
  }

  private goToCallScreen(): void {
    this.goToStep(ScaModuleStep.PhoneCalling);

    setTimeout(() => {
      this.goToStep(ScaModuleStep.CodeValidation);
    }, 5000);
  }

  private emitScaOptions(): void {
    if (this.step && this.initialized) {
      setTimeout(() => this.hasScaOptions.emit());
    }
  }

  private setUpPhoneSelection(): void {
    this.otpChannelFormControl = new FormControl(
      this.mobileNumber ? OtpChannel.Text : OtpChannel.Call,
      [Validators.required]
    );
    this.form.addControl('authCodePreference', this.otpChannelFormControl);
    this.goToStep(ScaModuleStep.PhoneSelect);
  }

  private getPasscodeValue(): PasscodeDigit[] {
    if (!this.form.controls[AuthenticationFactor.Passcode]) {
      return undefined;
    }

    return this.form.controls[AuthenticationFactor.Passcode].value;
  }

  private resetForm(): void {
    if (this.scaChallenge.passcodeChallenge) {
      this.initPasscodeField();
      this.goToStep(ScaModuleStep.Passcode);
    } else {
      this.goToStep(ScaModuleStep.PhoneSelect);
    }

    this.isResend = this.error = false;
    this.form.reset();
    this.isSubmitting = false;
    if (this.otpChannelFormControl) {
      this.otpChannelFormControl.setValue(this.getSelectedOtpChannel());
    }
  }

  private setErrorMessage(): void {
    this.isSubmitting = false;
    this.error = true;
  }

  private initPhoneNumbers(): void {
    this.accountSummaryService.getAccountSummary().then((result) => {
      this.mobileNumber = result.customer.mobileTelephone;
      this.homeNumber = result.customer.homeTelephone;
    });
  }

  private initPasscodeField(): void {
    if (!this.scaChallenge.passcodeChallenge) {
      return;
    }

    if (!this.passcodeFormControl) {
      this.passcodeFormControl = new FormControl('', [Validators.required]);
      this.form.addControl(
        AuthenticationFactor.Passcode,
        this.passcodeFormControl
      );
    }

    this.passcodeInputs = this.getPasscodeArray(
      this.scaChallenge.passcodeChallenge
    );
  }

  private setOtpAuthenticationFactor(): void {
    if (this.isSubmitting) {
      return;
    }

    this.authenticationFactor = AuthenticationFactor.OTP;
    this.form.addControl(AuthenticationFactor.OTP, new FormControl());
  }

  private getPasscodeArray(challenge: PasscodeChallenge): boolean[] {
    const arr = Array(challenge.passcodeLength).fill(false);

    challenge.passcodeDigits.forEach((digit) => {
      arr[digit.position] = true;
    });

    return arr;
  }

  private touchControls(): void {
    Object.keys(this.form.controls).forEach((key) =>
      this.form.controls[key].markAsTouched()
    );
  }

  private goToStep(step: ScaModuleStep): void {
    this.step = step;
    this.refreshModal();
  }

  private refreshModal(): void {
    setTimeout(() => {
      this.browserService.incrementResizeCount();
      this.popUpComponent.setFocusOrder();
    });
  }
}
